import { icon } from '../../icons';
import { i18nService, t, translateEventType, translatePropertyStatus, translateTimelineTitle } from '../../services/i18n';
import { type DashboardDto } from '../../services/api';

export const EVENT_TYPE_BADGE: Record<string, string> = {
  MoveIn: 'badge-success',
  MoveOut: 'badge-warning',
  Damage: 'badge-danger',
  Inspection: 'badge-info',
  Indexation: 'badge-info',
  Remark: 'badge-muted',
  PropertyStatusChange: 'badge-info',
};

export function esc(str: string): string {
  const el = document.createElement('span');
  el.textContent = str;
  return el.innerHTML;
}

export function formatDate(dateStr: string): string {
  return i18nService.formatDate(dateStr, { day: 'numeric', month: 'short', year: 'numeric' });
}

export function getDaysUntil(dueDate: string): number {
  return Math.ceil((new Date(dueDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}

export function dayLabel(days: number): string {
  if (days === 0) return t('common.today');
  if (days === 1) return t('common.tomorrow');
  return t('common.daysShort', { count: days });
}

export function renderLinkedStatCard(options: {
  href: string;
  iconHtml?: string;
  value: string | number;
  labelHtml: string;
  extraClassName?: string;
}): string {
  const classes = ['stat-card', 'stat-card-link'];
  if (options.extraClassName) classes.push(options.extraClassName);
  return `
    <a href="${options.href}" class="${classes.join(' ')}">
      ${options.iconHtml ?? ''}
      <div class="stat-card-body">
        <div class="stat-value">${options.value}</div>
        <div class="stat-label">${options.labelHtml}</div>
      </div>
    </a>
  `;
}

export function renderDashboardMarkup(data: DashboardDto): string {
  const buildingOccupancy = data.buildingOccupancy || [];
  return `
    <div class="stats-grid">
      ${renderLinkedStatCard({ href: '/properties/', iconHtml: `<div class="stat-icon stat-icon--blue">${icon('home', { size: 20 })}</div>`, value: data.totalProperties, labelHtml: t('nav.properties') })}
      ${renderLinkedStatCard({ href: '/tenants/', iconHtml: `<div class="stat-icon stat-icon--green">${icon('users', { size: 20 })}</div>`, value: data.totalActiveTenants, labelHtml: t('dashboard.activeTenants') })}
      ${renderLinkedStatCard({ href: '/reminders/', iconHtml: `<div class="stat-icon stat-icon--yellow">${icon('bell', { size: 20 })}</div>`, value: data.upcomingReminders.length, labelHtml: t('dashboard.upcomingDeadlines') })}
    </div>
    <div class="dashboard-section">
      <h2>${t('dashboard.propertyStatuses')}</h2>
      <div class="stats-grid">
        ${[
          ['Rented', data.propertyStatuses.rented, 'badge-success'],
          ['Vacant', data.propertyStatuses.vacant, 'badge-warning'],
          ['UnderRenovation', data.propertyStatuses.underRenovation, 'badge-info'],
          ['ForSale', data.propertyStatuses.forSale, 'badge-muted'],
        ].map(([status, count, badgeClass]) => renderLinkedStatCard({
          href: '/properties/',
          value: Number(count),
          labelHtml: `<span class="badge ${badgeClass}">${translatePropertyStatus(String(status))}</span>`,
        })).join('')}
      </div>
    </div>
    ${buildingOccupancy.length > 0 ? `
      <div class="dashboard-section">
        <h2>${t('dashboard.buildingOccupancy')}</h2>
        <div class="card">
          ${buildingOccupancy.map((building) => `
            <a class="list-item" href="/property/?property=${encodeURIComponent(building.propertyId)}&from=${encodeURIComponent('/dashboard/')}">
              <div>
                <div class="list-item-title">${esc(building.displayName)}</div>
                <div class="list-item-meta">${t('dashboard.buildingOccupancyMeta', { occupied: building.occupiedUnits, total: building.totalUnits })}</div>
              </div>
              <div class="list-item-right">
                <span class="badge ${building.occupiedUnits === building.totalUnits ? 'badge-success' : building.occupiedUnits === 0 ? 'badge-warning' : 'badge-info'}">${building.occupiedUnits}/${building.totalUnits}</span>
              </div>
            </a>
          `).join('')}
        </div>
      </div>
    ` : ''}
    <div class="dashboard-grid">
      <div class="dashboard-section">
        <h2>${t('dashboard.upcomingDeadlines')}</h2>
        <div class="card">
          ${data.upcomingReminders.length === 0
            ? `<div class="empty-state">${icon('bell', { size: 32, strokeWidth: 1.5, attrs: 'opacity="0.4"' })}<p>${t('dashboard.noUpcomingDeadlines')}</p></div>`
            : data.upcomingReminders.map((reminder) => {
              const days = getDaysUntil(reminder.dueDate);
              const urgentCls = days <= 3 ? 'reminder-urgent' : days <= 7 ? 'reminder-soon' : '';
              return `<div class="list-item ${urgentCls}"><div><div class="list-item-title">${esc(reminder.title)}</div>${reminder.propertyAddress ? `<div class="list-item-meta">${esc(reminder.propertyAddress)}</div>` : ''}</div><div class="list-item-right"><div class="list-item-date">${formatDate(reminder.dueDate)}</div><div class="list-item-days ${days <= 3 ? 'text-danger' : days <= 7 ? 'text-warning' : 'text-muted'}">${dayLabel(days)}</div></div></div>`;
            }).join('')}
        </div>
        ${data.upcomingReminders.length > 0 ? `<a href="/reminders/" class="view-all-link">${t('dashboard.viewAllReminders')} -></a>` : ''}
      </div>
      <div class="dashboard-section">
        <h2>${t('dashboard.recentEvents')}</h2>
        <div class="card">
          ${data.recentEvents.length === 0
            ? `<div class="empty-state">${icon('calendar', { size: 32, strokeWidth: 1.5, attrs: 'opacity="0.4"' })}<p>${t('dashboard.noRecentEvents')}</p></div>`
            : data.recentEvents.map((event) => `<div class="list-item"><div><div class="list-item-title">${esc(translateTimelineTitle(event.title, event.titleKey))}</div><div class="list-item-meta">${esc(event.subjectName)}</div></div><div class="list-item-right"><span class="badge ${EVENT_TYPE_BADGE[event.eventType] || 'badge-muted'}">${translateEventType(event.eventType)}</span><div class="list-item-date">${formatDate(event.date)}</div></div></div>`).join('')}
        </div>
        ${data.recentEvents.length > 0 ? `<a href="/tenants/" class="view-all-link">${t('dashboard.viewTenants')} -></a>` : ''}
      </div>
    </div>
  `;
}
